import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { getProjectKickoffRegistrations } from '../api';

const PROGRAMS = {
    'campus-improvement-grant': 'Campus Improvement Grant',
    'innovation-lounge': 'Innovation Lounge',
};
const PAGE_SIZE = 25;

export function csvValue(value) {
    const text = String(value ?? '').replace(/"/g, '""');
    return /[",\n\r]/.test(text) ? `"${text}"` : text;
}

export function registrationsToCsv(registrations) {
    const headings = ['Submitted', 'Program', 'Session', 'School', 'First Name', 'Last Name', 'Email', 'Phone'];
    const rows = registrations.map((registration) => [
        formatSubmittedAt(registration.submittedAt), PROGRAMS[registration.programType] || registration.programType,
        registration.sessionDateTime, registration.schoolName, registration.firstName, registration.lastName,
        registration.emailAddress, registration.phoneNumber,
    ].map(csvValue).join(','));
    return `\uFEFF${[headings.join(','), ...rows].join('\r\n')}`;
}

export function formatSubmittedAt(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : new Intl.DateTimeFormat('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    }).format(date);
}

function downloadCsv(csv, filename) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

export default function ProjectKickoffRegistrations() {
    const [registrations, setRegistrations] = React.useState(null);
    const [program, setProgram] = React.useState('all');
    const [search, setSearch] = React.useState('');
    const [page, setPage] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');

    const load = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getProjectKickoffRegistrations();
            setRegistrations(response.data || []);
        } catch (requestError) {
            setError(requestError?.response?.data?.message || 'Unable to load project kickoff registrations.');
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => { load(); }, [load]);
    React.useEffect(() => { setPage(0); }, [program, search]);

    const filtered = React.useMemo(() => {
        const query = search.trim().toLowerCase();
        return (registrations || []).filter((registration) => {
            const matchesProgram = program === 'all' || registration.programType === program;
            const matchesSearch = !query || [registration.schoolName, registration.firstName, registration.lastName,
            registration.emailAddress].some((value) => String(value || '').toLowerCase().includes(query));
            return matchesProgram && matchesSearch;
        });
    }, [registrations, program, search]);
    const visibleRows = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
    const exportFilename = program === 'all' ? 'project-kickoff-registrations.csv' : `${program}-kickoff-registrations.csv`;

    return <Box sx={{ width: '92%', maxWidth: '100%', minWidth: 0, mx: 'auto', py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
            <Box><Typography variant="h4" component="h1">Project Kickoff Registrations</Typography>
                <Typography color="text.secondary">{filtered.length} registration{filtered.length === 1 ? '' : 's'} matching current filters</Typography></Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
                <Button variant="contained" startIcon={<DownloadIcon />} disabled={!filtered.length} onClick={() => downloadCsv(registrationsToCsv(filtered), exportFilename)}>Export CSV</Button>
            </Box>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Select size="small" value={program} onChange={(event) => setProgram(event.target.value)} inputProps={{ 'aria-label': 'Program filter' }}>
                <MenuItem value="all">All</MenuItem><MenuItem value="campus-improvement-grant">Campus Improvement Grant</MenuItem><MenuItem value="innovation-lounge">Innovation Lounge</MenuItem>
            </Select>
            <TextField size="small" placeholder="Search school, name, or email" value={search} onChange={(event) => setSearch(event.target.value)} inputProps={{ 'aria-label': 'Search registrations' }} sx={{ minWidth: { xs: '100%', sm: 320 } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        </Box>
        {loading && registrations === null ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : <TableContainer aria-label="Project kickoff registrations table" tabIndex={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
            <Table sx={{ minWidth: 1100 }}><TableHead><TableRow>{['Submitted', 'Program', 'Session', 'School', 'First Name', 'Last Name', 'Email', 'Phone'].map((heading) => <TableCell key={heading} sx={{ fontWeight: 700 }}>{heading}</TableCell>)}</TableRow></TableHead>
                <TableBody>{visibleRows.map((registration, index) => <TableRow key={`${registration.emailAddress}-${registration.submittedAt}-${index}`} hover>
                    <TableCell>{formatSubmittedAt(registration.submittedAt)}</TableCell><TableCell>{PROGRAMS[registration.programType] || registration.programType}</TableCell><TableCell>{registration.sessionDateTime}</TableCell><TableCell>{registration.schoolName}</TableCell><TableCell>{registration.firstName}</TableCell><TableCell>{registration.lastName}</TableCell><TableCell><a href={`mailto:${registration.emailAddress}`}>{registration.emailAddress}</a></TableCell><TableCell><a href={`tel:${registration.phoneNumber}`}>{registration.phoneNumber}</a></TableCell>
                </TableRow>)}
                    {!visibleRows.length && <TableRow><TableCell colSpan={8} align="center">{registrations?.length ? 'No registrations match your search or filters.' : 'No project kickoff registrations have been submitted yet.'}</TableCell></TableRow>}
                </TableBody></Table>
            <TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, nextPage) => setPage(nextPage)} rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]} />
        </TableContainer>}
    </Box>;
}